import React, { useEffect, useState } from 'react';
import Fuse from 'fuse.js';
import Autocomplete from 'react-autocomplete';
import { random } from 'lodash';
import { MagentoMenuItem } from 'types/Navigation';
import { redirectToPage } from 'helpers/navigation';
import { Icon } from 'components';
import './ProductSearch.css';
import { trackSearchResultSelected } from 'analytics/itly/hooks';
import { isLocal } from 'helpers/env';

import 'features/pdp';
/*
  TODO ^this import^ (import 'features/pdp';) is a problem as we are not actually using it, however if we do not have it, circular dependency errors pop up like whackamole

  Bug ticket: https://artifactteam.atlassian.net/browse/EN-10996

  Previously this looked like: import { isCard } from 'features/pdp';
  When I removed that line I started getting all sorts of circular dependency errors
  
  We have an issue somewhere in our dependency chain that this import 'fixes' by being a "circular dependency stabilizer" 
  That is, it forces certain modules to load earlier, before the full circular loop is completed which changes the evaluation timing of the circular chain.
  
  My investigation shows that it is most likely around useProductForm, useProduct, and useProductPrice and the components that use those

  Using madge (https://www.npmjs.com/package/madge) I was able to find that we actually have 36 total circular dependencies!
    1) types/Product.ts > types/Category.ts
    2) features/pdp/index.ts > features/pdp/components/Form/PDPInput/index.ts > features/pdp/components/Form/PDPInput/InputContainer.tsx > features/pdp/components/Form/PDPInput/InputComponent.tsx > features/pdp/helpers/index.ts > features/pdp/helpers/form.ts
    3) features/pdp/helpers/index.ts > features/pdp/helpers/live-preview.ts > features/pdp/hooks/useProductForm.ts
    4) features/pdp/helpers/index.ts > features/pdp/helpers/live-preview.ts > features/pdp/hooks/useProductForm.ts > features/pdp/hooks/useProductPrice.ts
    5) features/pdp/hooks/useProductForm.ts > features/pdp/hooks/useProductPrice.ts
    6) features/pdp/index.ts > features/pdp/components/Form/PDPInput/index.ts > features/pdp/components/Form/PDPInput/InputContainer.tsx > features/pdp/components/Form/PDPInput/InputComponent.tsx > features/pdp/helpers/index.ts > features/pdp/helpers/live-preview.ts > features/pdp/hooks/useProductForm.ts > hooks/splits/useShouldSkipLoginGate.ts
    7) store/cart/constants.ts > store/cart/actions.ts
    8) store/constants.ts > store/cart/constants.ts
    9) store/constants.ts > store/customer/constants.ts
    10) store/constants.ts > store/ui/constants.ts
    11) features/pdp/helpers/index.ts > features/pdp/helpers/selectors.ts > features/pdp/hooks/useProductUseCase.ts
    12) features/pdp/index.ts > features/pdp/components/PDP.tsx > features/pdp/components/PdpAccordion.tsx
    13) analytics/itly/analyticsWrapper.ts > analytics/itly/beacons/mapApplyPromoCodeErrorToEvent.ts
    14) analytics/itly/analyticsWrapper.ts > analytics/itly/beacons/mapApplyPromoCodeToEvent.ts
    15) analytics/itly/analyticsWrapper.ts > analytics/itly/beacons/mapCheckoutErrorActionToEvent.ts
    16) analytics/itly/analyticsWrapper.ts > analytics/itly/beacons/mapCheckoutStepShippingCompletedActionToEvent.ts
    17) analytics/itly/analyticsWrapper.ts > analytics/itly/beacons/mapCheckoutStepShippingCompletedActionToEvent.ts > analytics/itly/tracking.ts
    18) analytics/itly/analyticsWrapper.ts > analytics/itly/beacons/mapFirstPurchaseActionToEvent.ts
    19) analytics/itly/analyticsWrapper.ts > analytics/itly/beacons/mapNewCardAddedActionToEvent.ts
    20) analytics/itly/analyticsWrapper.ts > analytics/itly/beacons/mapPlaceOrderSuccessActionToEvent.ts
    21) analytics/itly/analyticsWrapper.ts > analytics/itly/beacons/mapProductAddedActionToEvent.ts
    22) components/AddressLists/ContactGroups/index.tsx > components/AddressLists/index.tsx
    23) components/AddressLists/index.tsx > components/AddressLists/ContactList/index.tsx
    24) components/CLP/styled/index.ts > components/CLP/CategoryHeader.tsx
    25) components/CLPFilters/helpers.ts > components/CLPFilters/index.tsx > components/CLPFilters/FilterPill.tsx
    26) components/CLPFilters/helpers.ts > components/CLPFilters/index.tsx
    27) components/index.ts > components/Forms/TextInput/index.tsx
    28) components/index.ts > components/GenericOverlay/index.tsx
    29) components/GoogleReCaptcha/index.ts > components/GoogleReCaptcha/GoogleReCaptcha.ts > components/hooks/google-re-captcha/index.ts > components/hooks/google-re-captcha/useGoogleReCaptcha.ts > components/hooks/google-re-captcha/useGoogleReCaptchaContext.ts
    30) components/CreatePassword/CreatePasswordForm.tsx > components/CreatePassword/index.tsx
    31) components/GalleriesManagement/GalleryModal/UploadPhotosModal.tsx > components/GalleriesManagement/index.tsx > components/GalleriesManagement/GalleryToolbar/index.tsx > components/GalleriesManagement/GalleryModal/index.tsx
    32) components/MyDetails/EditUserForm.tsx > components/MyDetails/helpers.ts
    33) store/cart/magentoSelectors.ts > store/cart/helpers.tsx
    34) components/SegmentConsentManager/container.tsx > components/SegmentConsentManager/banner.tsx > components/SegmentConsentManager/types.ts
    35) store/cart/sagas.ts > store/sagas.ts
    36) store/sagas.ts > store/customer/sagas.ts
*/

export interface ProductSearchProps {
  cancelSearch?: () => void;
}

type SearchItem = {
  name: string;
  url: string;
  sku: string;
  type: string;
  category: string;
};

const FUSE_SEARCH_SIMILARITY_THRESHOLD = 0.42; // how fuzzy (0 = exact, 1 = anything), default is 0.6

const ProductSearch = ({ cancelSearch }: ProductSearchProps): JSX.Element => {
  const [searchParam, setSearchParam] = useState('');
  const [items, setItems] = useState<SearchItem[]>([]);
  const [fuse, setFuse] = useState<Fuse<any> | null>(null);

  const wrapperStyle = { width: '100%' };
  const menuStyle = { width: '100%' };

  useEffect(() => {
    const searchableProductsUrl = `${isLocal() ? '' : '/gatsbyassets'}/searchable-products.json`;
    fetch(searchableProductsUrl)
      .then((res) => res.json())
      .then((products) => {
        setFuse(new Fuse(products, { keys: ['name'], threshold: FUSE_SEARCH_SIMILARITY_THRESHOLD }));
      })
      .catch((e) => {
        throw new Error('Something went wrong!\nPlease try again later.');
      });
  }, []);

  const filterList = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.currentTarget.value;
    setSearchParam(query);

    if (!fuse) {
      return;
    }

    const results = fuse.search(query).map((result) => result.item);
    setItems(results);
  };

  const navigateToSelection = (value: string): void => {
    const foundItem = items.find((item) => item.name === value);
    if (foundItem) {
      trackSearchResultSelected(searchParam, foundItem.name);
      redirectToPage(foundItem.url);
      cancelSearch?.();
    }
  };

  const renderItem = (item: MagentoMenuItem) => (
    <div
      className="productSearchResult"
      key={item.name + random(1, 1000)}
      onClick={() => navigateToSelection(item.name)}
    >
      <span>{item.name}</span>
      <Icon className="rightArrow" icon="arrow" />
    </div>
  );

  const renderMenu = (items: any[], value: string) => (
    <div className="menu autocompleteContainer">
      {value === '' ? (
        <div className="defaultText" onClick={cancelSearch}>
          <div className="quoteText">We take photos as a return ticket to a moment otherwise gone.</div>
        </div>
      ) : (
        <>
          <div className="productCount">Showing {items.length} product results</div>
          {items}
        </>
      )}
    </div>
  );

  return (
    <div className="productSearchWrapper">
      <button onClick={cancelSearch} className="cancelLink button-link">
        <Icon icon="close" className="productSearchClose" size="medium" />
      </button>
      <Autocomplete
        menuStyle={menuStyle}
        getItemValue={(item) => item.name}
        wrapperStyle={wrapperStyle}
        items={items}
        renderItem={renderItem}
        renderMenu={renderMenu}
        inputProps={{
          autoFocus: true,
          placeholder: 'Search...',
          style: {
            fontFamily: '"Lato", sans-serif',
            letterSpacing: '.5px',
            border: '1px solid',
            borderColor: 'rgb(209, 209, 209)',
            width: '100%',
            height: '54px',
            marginBottom: '15px',
            borderRadius: '4px',
            paddingLeft: '10px',
            boxShadow:
              '0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px rgba(0, 0, 0, 0.12), 0px 2px 4px rgba(0, 0, 0, 0.2)'
          }
        }}
        value={searchParam}
        onChange={filterList}
        onSelect={navigateToSelection}
      />
    </div>
  );
};

export default ProductSearch;
